import { FC, useState } from "react";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

import { ProductionSceneImageResource } from "@/types/resources";
import Text from "@/components/Text";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import ImageFormModal from "./ImageFormModal";

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  padding: 16px;
  display: flex;
  position: relative;
`;

const Image = styled.img`
  width: 280px;
  height: auto;
  margin-right: 24px;
`;

const Description = styled(Text)`
  flex: 1;
  font-size: 12px;
  line-height: 18px;
  margin-right: 16px;
  text-align: left;
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  z-index: 51;
`;

interface Props {
  image: ProductionSceneImageResource;
}

const ImagesModalCard: FC<Props> = ({ image }) => {
  const [showEditModal, setShowEditModal] = useState(false);

  const alerts = useAlerts();

  const { projectId } = useParams();

  const queryClient = useQueryClient();

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete this image?"
      );

      if (!confirmed) return;

      await api.delete(`/v1/production/scenes/images/${image.id}`);

      await queryClient.invalidateQueries([
        "production_projects",
        { projectId },
      ]);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };
  return (
    <Card>
      <Image
        src={image.image.versions.medium ?? image.image.versions.original}
        alt=""
      />
      <Description $type="smallMedium">{image.description}</Description>
      <StyledActionsDropdown>
        <ActionsDropdownButton
          as="button"
          type="button"
          onClick={() => setShowEditModal(true)}
        >
          <PencilIcon />
          Edit
        </ActionsDropdownButton>
        <ActionsDropdownButton type="button" onClick={onDelete} as="button">
          <TrashIcon />
          Delete
        </ActionsDropdownButton>
      </StyledActionsDropdown>
      <AnimatePresence>
        {showEditModal && (
          <ImageFormModal
            onClose={() => setShowEditModal(false)}
            editableSceneImage={image}
          />
        )}
      </AnimatePresence>
    </Card>
  );
};

export default ImagesModalCard;
